import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { LogoUploader } from "@/components/LogoUploader";
import { EmailDelivery } from "@/components/EmailDelivery";
import { SubscriptionSummary } from "@/components/SubscriptionSummary";
import { DeleteAccountDialog } from "@/components/DeleteAccountDialog";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";

const Section = ({ title, desc, children }) => (
  <section className="surface p-4 sm:p-6 grid lg:grid-cols-[240px_1fr] gap-4 lg:gap-8 min-w-0 overflow-hidden">
    <div><h2 className="text-lg font-semibold">{title}</h2><p className="text-sm text-muted-foreground mt-1">{desc}</p></div>
    <div className="space-y-4 min-w-0">{children}</div>
  </section>
);

export default function SettingsPage() {
  const { setUser } = useAuth();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.put("/settings", { business_name: s.business_name, address: s.address, phone: s.phone, email: s.email, invoice_prefix: s.invoice_prefix, notes: s.notes, monthly_receipt_email: s.monthly_receipt_email !== false, tax_rate: Number(s.tax_rate) || 0, next_invoice_number: Number(s.next_invoice_number) || 1 });
      setS(r.data);
      setUser((u) => ({ ...u, business_name: r.data.business_name }));
      toast.success("Settings saved");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  if (!s) return <div className="text-sm text-muted-foreground">Loading settings...</div>;

  return (
    <form onSubmit={save} className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Company Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">This info prints on every invoice you send.</p>
      </div>

      <Section title="Subscription" desc="Your Basta10 plan and billing.">
        <SubscriptionSummary />
      </Section>

      <Section title="Email delivery" desc="Where invoices and receipts are sent from. Verify your own domain so any client can receive them.">
        <EmailDelivery />
      </Section>

      <Section title="Company logo" desc="Prints at the top of every invoice PDF and email.">
        <LogoUploader settings={s} onChange={setS} />
      </Section>

      <Section title="Business info" desc="Appears in the invoice header and email footer.">
        <div className="space-y-1.5"><Label>Business name</Label><Input data-testid="settings-business-name-input" required value={s.business_name} onChange={set("business_name")} /></div>
        <div className="space-y-1.5"><Label>Address</Label><Input data-testid="settings-address-input" value={s.address} onChange={set("address")} placeholder="123 Main St, Austin, TX 78701" /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="settings-phone-input" value={s.phone} onChange={set("phone")} /></div>
          <div className="space-y-1.5"><Label>Business email (reply-to)</Label><Input data-testid="settings-email-input" type="email" value={s.email} onChange={set("email")} /></div>
        </div>
      </Section>

      <Section title="Tax & numbering" desc="Your default tax rate. Individual clients can override it.">
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-1.5"><Label>Tax rate %</Label><Input data-testid="settings-tax-rate-input" type="number" step="0.001" min="0" max="100" value={s.tax_rate} onChange={set("tax_rate")} className="font-mono" /></div>
          <div className="space-y-1.5"><Label>Invoice prefix</Label><Input data-testid="settings-invoice-prefix-input" value={s.invoice_prefix} onChange={set("invoice_prefix")} className="font-mono" /></div>
          <div className="space-y-1.5"><Label>Next number</Label><Input data-testid="settings-next-number-input" type="number" min="1" value={s.next_invoice_number} onChange={set("next_invoice_number")} className="font-mono" /></div>
        </div>
        <p className="text-xs text-muted-foreground">Next invoice will be <span className="font-mono font-bold">{s.invoice_prefix}{String(s.next_invoice_number).padStart(4, "0")}</span></p>
      </Section>

      <Section title="Invoice notes" desc="Payment terms or a thank-you, printed at the bottom of every invoice.">
        <Textarea data-testid="settings-notes-input" rows={4} value={s.notes} onChange={set("notes")} placeholder="Payment due upon receipt. Zelle: 555-123-4567" />
      </Section>

      <Button data-testid="settings-save-button" type="submit" disabled={busy} className="h-11 px-8 bg-emerald-600 hover:bg-emerald-700 text-white font-bold">{busy ? "Saving..." : "Save Settings"}</Button>

      <Section title="Monthly receipts email" desc="On the 1st of each month we email you last month's receipt photos as a ZIP (named by date · category · amount, with a CSV) plus a printable 8-per-page PDF.">
        <label className="flex items-center gap-3 text-sm font-medium cursor-pointer">
          <input type="checkbox" data-testid="settings-monthly-receipts-toggle" checked={s.monthly_receipt_email !== false} onChange={(e) => setS({ ...s, monthly_receipt_email: e.target.checked })} className="h-5 w-5 accent-emerald-600" />
          Email me my receipts bundle every month
        </label>
      </Section>

      <Section title="Help & legal" desc="Support, privacy policy and terms.">
        <p className="text-sm flex flex-wrap gap-x-4 gap-y-1">
          <Link to="/support" className="font-semibold text-emerald-700 dark:text-emerald-400" data-testid="settings-support-link">Support</Link>
          <Link to="/privacy" className="font-semibold text-emerald-700 dark:text-emerald-400" data-testid="settings-privacy-link">Privacy Policy</Link>
          <Link to="/terms" className="font-semibold text-emerald-700 dark:text-emerald-400" data-testid="settings-terms-link">Terms of Service</Link>
        </p>
      </Section>

      <Section title="Delete account" desc="Permanently remove your business and all of its data.">
        <p className="text-sm text-muted-foreground">Deletes every client, invoice, expense, receipt photo and your logo, and cancels any active subscription. This cannot be undone.</p>
        <Button type="button" variant="outline" onClick={() => setDeleting(true)} className="border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40" data-testid="settings-delete-account-button">Delete my account…</Button>
        <DeleteAccountDialog open={deleting} onOpenChange={setDeleting} />
      </Section>
    </form>
  );
}
